import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { leaguesApi, blindStructuresApi } from '../services/api';
import { shouldUseMockApi } from '../services/mockBlindStructures';
import './BlindStructures.css';

function BlindStructures({ user, onLogout }) {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [structures, setStructures] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [structureFormData, setStructureFormData] = useState({
    name: '',
    description: '',
    blindDuration: 20,
    breakDuration: 10,
    breakFrequency: 4,
  });

  // Level inline editing state
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [levelFormData, setLevelFormData] = useState({
    levelNumber: 1,
    smallBlind: 25,
    bigBlind: 50,
    ante: 0,
    isBreak: false,
    durationSeconds: 1200,
    breakDurationSeconds: 600,
  });
  const [bigBlindLinked, setBigBlindLinked] = useState(true);
  const [levelLengthEnabled, setLevelLengthEnabled] = useState(true);
  const [overrideAllLengths, setOverrideAllLengths] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [leagueId]);

  useEffect(() => {
    if (selectedStructure) {
      setSelectedLevel(null);
      fetchLevels(selectedStructure.id);
    }
  }, [selectedStructure]);

  // Auto-select first level when levels load
  useEffect(() => {
    if (levels.length > 0 && !selectedLevel) {
      selectLevel(levels.sort((a, b) => (a.levelNumber || a.level_number) - (b.levelNumber || b.level_number))[0]);
    }
  }, [levels]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [leagueRes, structuresRes] = await Promise.all([
        leaguesApi.getById(leagueId),
        leaguesApi.getBlindStructures(leagueId),
      ]);

      setLeague(leagueRes.data?.league || leagueRes.league || leagueRes.data);
      const structuresList = structuresRes.data?.blindStructures || structuresRes.blindStructures || structuresRes.data || [];
      setStructures(Array.isArray(structuresList) ? structuresList : []);

      // Select first structure by default
      if (structuresList.length > 0 && !selectedStructure) {
        setSelectedStructure(structuresList[0]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load blind structures');
    } finally {
      setLoading(false);
    }
  };

  const fetchLevels = async (structureId) => {
    try {
      const response = await blindStructuresApi.getLevels(structureId);
      const levelsList = response.data?.levels || response.levels || response.data || [];
      setLevels(Array.isArray(levelsList) ? levelsList : []);
    } catch (err) {
      console.error('Error fetching levels:', err);
      setError('Failed to load blind levels');
    }
  };

  // --- Structure modal handlers (unchanged) ---

  const handleOpenStructureModal = (structure = null) => {
    if (structure) {
      setEditingStructure(structure);
      setStructureFormData({
        name: structure.name || '',
        description: structure.description || '',
        blindDuration: structure.blindDuration || structure.blind_duration_minutes || 20,
        breakDuration: structure.breakDuration || structure.break_duration_minutes || 10,
        breakFrequency: structure.breakFrequency || structure.break_frequency || 4,
      });
    } else {
      setEditingStructure(null);
      setStructureFormData({
        name: '',
        description: '',
        blindDuration: league?.defaultBlindDuration || league?.default_blind_duration_minutes || 20,
        breakDuration: league?.defaultBreakDuration || league?.default_break_duration_minutes || 10,
        breakFrequency: league?.defaultBreakFrequency || league?.default_break_frequency || 4,
      });
    }
    setShowStructureModal(true);
  };

  const handleCloseStructureModal = () => {
    setShowStructureModal(false);
    setEditingStructure(null);
  };

  const handleStructureInputChange = (e) => {
    const { name, value } = e.target;
    setStructureFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStructureSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const structureData = {
        name: structureFormData.name,
        description: structureFormData.description || null,
        blindDuration: parseInt(structureFormData.blindDuration),
        breakDuration: parseInt(structureFormData.breakDuration),
        breakFrequency: parseInt(structureFormData.breakFrequency),
      };

      if (editingStructure) {
        await leaguesApi.updateBlindStructure(leagueId, editingStructure.id, structureData);
      } else {
        const response = await leaguesApi.createBlindStructure(leagueId, structureData);
        const newStructure = response.data?.blindStructure || response.blindStructure || response.data;
        setSelectedStructure(newStructure);
      }

      handleCloseStructureModal();
      fetchData();
    } catch (err) {
      console.error('Error saving blind structure:', err);
      setError('Failed to save blind structure');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStructure = async (structureId) => {
    if (!window.confirm('Are you sure you want to delete this blind structure? This will affect all games using it.')) {
      return;
    }

    try {
      await leaguesApi.deleteBlindStructure(leagueId, structureId);
      if (selectedStructure?.id === structureId) {
        setSelectedStructure(null);
        setLevels([]);
        setSelectedLevel(null);
      }
      fetchData();
    } catch (err) {
      console.error('Error deleting blind structure:', err);
      setError('Failed to delete blind structure');
    }
  };

  const handleSetDefault = async (structureId) => {
    try {
      await leaguesApi.setDefaultBlindStructure(leagueId, structureId);
      fetchData();
    } catch (err) {
      console.error('Error setting default structure:', err);
      setError('Failed to set default structure');
    }
  };

  // --- Level inline editing handlers ---

  const selectLevel = (level) => {
    const isBreak = level.isBreak || level.is_break;
    const sb = level.smallBlind || level.small_blind || 0;
    const bb = level.bigBlind || level.big_blind || 0;
    const duration = isBreak
      ? (level.breakDurationSeconds || level.break_duration_seconds || 600)
      : (level.durationSeconds || level.duration_seconds || 1200);

    setSelectedLevel(level);
    setLevelFormData({
      levelNumber: level.levelNumber || level.level_number || 1,
      smallBlind: sb,
      bigBlind: bb,
      ante: 0,
      isBreak: isBreak,
      durationSeconds: isBreak ? 1200 : duration,
      breakDurationSeconds: isBreak ? duration : 600,
    });
    setBigBlindLinked(bb === sb * 2);
    setLevelLengthEnabled(true);
    setOverrideAllLengths(false);
  };

  const handleSaveLevel = async () => {
    if (!selectedStructure || !selectedLevel) return;
    setSaving(true);

    try {
      const levelData = {
        levelNumber: parseInt(levelFormData.levelNumber),
        smallBlind: parseInt(levelFormData.smallBlind) || 0,
        bigBlind: bigBlindLinked
          ? (parseInt(levelFormData.smallBlind) || 0) * 2
          : (parseInt(levelFormData.bigBlind) || 0),
        ante: 0,
        isBreak: levelFormData.isBreak,
        durationSeconds: parseInt(levelFormData.durationSeconds) || 1200,
        breakDurationSeconds: parseInt(levelFormData.breakDurationSeconds) || 600,
      };

      await blindStructuresApi.updateLevel(selectedStructure.id, selectedLevel.id, levelData);

      // If overrideAllLengths is checked, update all levels' durations
      if (overrideAllLengths) {
        const durationToApply = levelFormData.isBreak
          ? levelData.breakDurationSeconds
          : levelData.durationSeconds;

        const promises = levels
          .filter(l => l.id !== selectedLevel.id)
          .map(l => {
            const isLevelBreak = l.isBreak || l.is_break;
            // Only override same-type levels (blind duration for blinds, break duration for breaks)
            if (isLevelBreak === levelFormData.isBreak) {
              const updateData = isLevelBreak
                ? { ...l, breakDurationSeconds: durationToApply }
                : { ...l, durationSeconds: durationToApply };
              return blindStructuresApi.updateLevel(selectedStructure.id, l.id, updateData);
            }
            return Promise.resolve();
          });
        await Promise.all(promises);
      }

      const savedLevelId = selectedLevel.id;
      await fetchLevels(selectedStructure.id);
      // Re-select the same level after refresh
      setSelectedLevel(prev => {
        const refreshed = levels.find(l => l.id === savedLevelId);
        return refreshed || prev;
      });
    } catch (err) {
      console.error('Error saving level:', err);
      setError('Failed to save blind level');
    } finally {
      setSaving(false);
      setOverrideAllLengths(false);
    }
  };

  const handleAddLevel = async () => {
    if (!selectedStructure) return;
    const sortedLevels = [...levels].sort(
      (a, b) => (a.levelNumber || a.level_number) - (b.levelNumber || b.level_number)
    );
    const nextNumber = sortedLevels.length > 0
      ? (sortedLevels[sortedLevels.length - 1].levelNumber || sortedLevels[sortedLevels.length - 1].level_number) + 1
      : 1;

    // Derive SB from last non-break level
    const lastBlindLevel = [...sortedLevels].reverse().find(l => !(l.isBreak || l.is_break));
    const newSB = lastBlindLevel ? (lastBlindLevel.smallBlind || lastBlindLevel.small_blind || 25) * 2 : 25;

    const defaultDuration = selectedStructure.blindDuration
      ? selectedStructure.blindDuration * 60
      : (selectedStructure.blind_duration_minutes ? selectedStructure.blind_duration_minutes * 60 : 1200);

    try {
      const response = await blindStructuresApi.createLevel(selectedStructure.id, {
        levelNumber: nextNumber,
        smallBlind: newSB,
        bigBlind: newSB * 2,
        ante: 0,
        isBreak: false,
        durationSeconds: defaultDuration,
        breakDurationSeconds: 600,
      });
      await fetchLevels(selectedStructure.id);
      // Select the newly created level
      const newLevel = response.data?.level || response.level;
      if (newLevel) selectLevel(newLevel);
    } catch (err) {
      console.error('Error adding level:', err);
      setError('Failed to add level');
    }
  };

  const handleAddBreak = async () => {
    if (!selectedStructure) return;
    const sortedLevels = [...levels].sort(
      (a, b) => (a.levelNumber || a.level_number) - (b.levelNumber || b.level_number)
    );
    const nextNumber = sortedLevels.length > 0
      ? (sortedLevels[sortedLevels.length - 1].levelNumber || sortedLevels[sortedLevels.length - 1].level_number) + 1
      : 1;

    const defaultBreakDuration = selectedStructure.breakDuration
      ? selectedStructure.breakDuration * 60
      : (selectedStructure.break_duration_minutes ? selectedStructure.break_duration_minutes * 60 : 600);

    try {
      const response = await blindStructuresApi.createLevel(selectedStructure.id, {
        levelNumber: nextNumber,
        smallBlind: 0,
        bigBlind: 0,
        ante: 0,
        isBreak: true,
        durationSeconds: 1200,
        breakDurationSeconds: defaultBreakDuration,
      });
      await fetchLevels(selectedStructure.id);
      const newLevel = response.data?.level || response.level;
      if (newLevel) selectLevel(newLevel);
    } catch (err) {
      console.error('Error adding break:', err);
      setError('Failed to add break');
    }
  };

  const handleDeleteLevel = async (levelId) => {
    if (!levelId || !window.confirm('Are you sure you want to delete this level?')) {
      return;
    }

    try {
      await blindStructuresApi.deleteLevel(selectedStructure.id, levelId);
      if (selectedLevel?.id === levelId) {
        setSelectedLevel(null);
      }
      fetchLevels(selectedStructure.id);
    } catch (err) {
      console.error('Error deleting blind level:', err);
      setError('Failed to delete blind level');
    }
  };

  // --- Elapsed time helpers ---

  const getElapsedTimes = (levelId) => {
    const sorted = [...levels].sort(
      (a, b) => (a.levelNumber || a.level_number) - (b.levelNumber || b.level_number)
    );
    let elapsed = 0;
    for (const level of sorted) {
      const isBreak = level.isBreak || level.is_break;
      const duration = isBreak
        ? (level.breakDurationSeconds || level.break_duration_seconds || 600)
        : (level.durationSeconds || level.duration_seconds || 1200);
      if (level.id === levelId) {
        return {
          startSeconds: elapsed,
          endSeconds: elapsed + duration,
        };
      }
      elapsed += duration;
    }
    return { startSeconds: 0, endSeconds: 0 };
  };

  const formatElapsedTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // --- Stepper helpers ---

  const adjustSmallBlind = (delta) => {
    const current = parseInt(levelFormData.smallBlind) || 0;
    const newVal = Math.max(0, current + delta);
    setLevelFormData(prev => ({
      ...prev,
      smallBlind: newVal,
      bigBlind: bigBlindLinked ? newVal * 2 : prev.bigBlind,
    }));
  };

  const adjustDuration = (delta) => {
    const field = levelFormData.isBreak ? 'breakDurationSeconds' : 'durationSeconds';
    const current = levelFormData[field] || 1200;
    const newVal = Math.max(60, current + delta);
    setLevelFormData(prev => ({ ...prev, [field]: newVal }));
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="admin-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="main-content">
          <div className="loading">
            <div className="loading-spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  const sortedLevels = [...levels].sort(
    (a, b) => (a.levelNumber || a.level_number) - (b.levelNumber || b.level_number)
  );

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <div>
            <button className="btn btn-secondary" onClick={() => navigate('/leagues')} style={{ marginBottom: '8px' }}>
              ← Back to Leagues
            </button>
            <h2>Blind Structures - {league?.name}</h2>
            <p>Manage blind structures and levels for your league</p>
          </div>
        </div>

        {shouldUseMockApi() && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <div>
              <strong>Development Mode:</strong> Using mock data (backend API not available).
              Changes will not persist after page reload.
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="blind-structures-layout">
          {/* Left Panel - Structures List */}
          <div className="card structures-panel">
            <div className="card-header">
              <h3>Structures</h3>
              <button className="btn btn-primary btn-sm" onClick={() => handleOpenStructureModal()}>
                + New
              </button>
            </div>

            {structures.length === 0 ? (
              <div className="empty-state" style={{ padding: '20px' }}>
                <p>No blind structures yet</p>
                <button className="btn btn-primary btn-sm" onClick={() => handleOpenStructureModal()}>
                  Create First Structure
                </button>
              </div>
            ) : (
              <div className="structures-list">
                {structures.map(structure => (
                  <div
                    key={structure.id}
                    className={`structure-item ${selectedStructure?.id === structure.id ? 'active' : ''}`}
                    onClick={() => setSelectedStructure(structure)}
                  >
                    <div className="structure-info">
                      <h4>{structure.name}</h4>
                      {(structure.isDefault || structure.is_default) && (
                        <span className="badge badge-success" style={{ fontSize: '11px', marginLeft: '8px' }}>Default</span>
                      )}
                      <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                        {structure.blindDuration || structure.blind_duration_minutes}min levels,
                        {structure.breakDuration || structure.break_duration_minutes}min breaks
                      </p>
                    </div>
                    <div className="structure-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenStructureModal(structure);
                        }}
                      >
                        Edit
                      </button>
                      {!(structure.isDefault || structure.is_default) && (
                        <button
                          className="btn btn-success btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetDefault(structure.id);
                          }}
                          title="Set as default"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteStructure(structure.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Level Editor */}
          <div className="card levels-panel">
            {selectedStructure ? (
              <div className="levels-editor-layout">
                {/* Header */}
                <div className="levels-editor-header">
                  <h3>Blind Levels</h3>
                  <p>{selectedStructure.name}</p>
                </div>

                <div className="levels-editor-body">
                  {/* Sub-left: Compact Level List */}
                  <div className="levels-list-panel">
                    {levels.length === 0 ? (
                      <div className="empty-state" style={{ padding: '20px' }}>
                        <p>No levels yet</p>
                        <p style={{ fontSize: '13px', color: '#666' }}>Use the buttons below to add levels</p>
                      </div>
                    ) : (
                      <div className="levels-compact-list">
                        {sortedLevels.map(level => {
                          const isBreak = level.isBreak || level.is_break;
                          const sb = level.smallBlind || level.small_blind || 0;
                          const bb = level.bigBlind || level.big_blind || 0;
                          const duration = isBreak
                            ? (level.breakDurationSeconds || level.break_duration_seconds || 600)
                            : (level.durationSeconds || level.duration_seconds || 1200);
                          const durationMinutes = Math.floor(duration / 60);

                          return (
                            <div
                              key={level.id}
                              className={`level-list-item ${isBreak ? 'break-item' : ''} ${selectedLevel?.id === level.id ? 'selected' : ''}`}
                              onClick={() => selectLevel(level)}
                            >
                              <span className="level-number">{level.levelNumber || level.level_number})</span>
                              <span className="level-blinds">
                                {isBreak ? 'BREAK' : `${sb.toLocaleString()} / ${bb.toLocaleString()}`}
                              </span>
                              <span className="level-duration">{durationMinutes} mins</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Sub-right: Inline Edit Form */}
                  <div className="level-edit-panel">
                    {selectedLevel ? (
                      <div className="level-edit-form">
                        <div className="level-edit-header">
                          <h4>{levelFormData.isBreak ? `Break ${levelFormData.levelNumber}` : `Blinds Level ${levelFormData.levelNumber}`}</h4>
                        </div>

                        {/* Small Blind (non-break only) */}
                        {!levelFormData.isBreak && (
                          <div className="form-group">
                            <label className="form-label">Small Blind</label>
                            <div className="stepper-input">
                              <button type="button" className="stepper-btn" onClick={() => adjustSmallBlind(-25)}>-</button>
                              <input
                                type="number"
                                className="stepper-value"
                                value={levelFormData.smallBlind}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  setLevelFormData(prev => ({
                                    ...prev,
                                    smallBlind: val,
                                    bigBlind: bigBlindLinked ? val * 2 : prev.bigBlind,
                                  }));
                                }}
                              />
                              <button type="button" className="stepper-btn" onClick={() => adjustSmallBlind(25)}>+</button>
                            </div>
                          </div>
                        )}

                        {/* Big Blind (non-break only) */}
                        {!levelFormData.isBreak && (
                          <div className="form-group">
                            <label className="form-label">Big Blind</label>
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={bigBlindLinked}
                                onChange={(e) => {
                                  setBigBlindLinked(e.target.checked);
                                  if (e.target.checked) {
                                    setLevelFormData(prev => ({
                                      ...prev,
                                      bigBlind: (parseInt(prev.smallBlind) || 0) * 2,
                                    }));
                                  }
                                }}
                              />
                              2 x Small Blind
                            </label>
                            {!bigBlindLinked && (
                              <div className="stepper-input" style={{ marginTop: '8px' }}>
                                <button type="button" className="stepper-btn" onClick={() => {
                                  setLevelFormData(prev => ({ ...prev, bigBlind: Math.max(0, (parseInt(prev.bigBlind) || 0) - 25) }));
                                }}>-</button>
                                <input
                                  type="number"
                                  className="stepper-value"
                                  value={levelFormData.bigBlind}
                                  onChange={(e) => setLevelFormData(prev => ({ ...prev, bigBlind: parseInt(e.target.value) || 0 }))}
                                />
                                <button type="button" className="stepper-btn" onClick={() => {
                                  setLevelFormData(prev => ({ ...prev, bigBlind: (parseInt(prev.bigBlind) || 0) + 25 }));
                                }}>+</button>
                              </div>
                            )}
                            {bigBlindLinked && (
                              <div className="linked-value">{(parseInt(levelFormData.smallBlind) || 0) * 2}</div>
                            )}
                          </div>
                        )}

                        {/* Level Length */}
                        <div className="form-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={levelLengthEnabled}
                              onChange={(e) => setLevelLengthEnabled(e.target.checked)}
                            />
                            Set Level Length
                          </label>
                          {levelLengthEnabled && (
                            <>
                              <div className="stepper-input" style={{ marginTop: '8px' }}>
                                <button type="button" className="stepper-btn" onClick={() => adjustDuration(-60)}>-</button>
                                <span className="stepper-value stepper-display">
                                  {Math.floor((levelFormData.isBreak ? levelFormData.breakDurationSeconds : levelFormData.durationSeconds) / 60)}
                                </span>
                                <button type="button" className="stepper-btn" onClick={() => adjustDuration(60)}>+</button>
                              </div>
                              <label className="checkbox-label" style={{ marginTop: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={overrideAllLengths}
                                  onChange={(e) => setOverrideAllLengths(e.target.checked)}
                                />
                                Override all Level Lengths
                              </label>
                            </>
                          )}
                        </div>

                        {/* Elapsed Times */}
                        <div className="elapsed-times">
                          <h5>Elapsed Times for Level {levelFormData.levelNumber}:</h5>
                          <div className="elapsed-row">
                            <span className="elapsed-label">Start:</span>
                            <span className="elapsed-value">{formatElapsedTime(getElapsedTimes(selectedLevel.id).startSeconds)}</span>
                          </div>
                          <div className="elapsed-row">
                            <span className="elapsed-label">End:</span>
                            <span className="elapsed-value">{formatElapsedTime(getElapsedTimes(selectedLevel.id).endSeconds)}</span>
                          </div>
                        </div>

                        {/* Save Button */}
                        <button
                          className="btn btn-primary"
                          onClick={handleSaveLevel}
                          disabled={saving}
                          style={{ width: '100%', marginTop: '16px' }}
                        >
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    ) : (
                      <div className="empty-state" style={{ padding: '20px', textAlign: 'center' }}>
                        <p>Select a level to edit</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Action Bar */}
                <div className="levels-action-bar">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteLevel(selectedLevel?.id)}
                    disabled={!selectedLevel}
                  >
                    Delete Level
                  </button>
                  <div className="action-bar-right">
                    <button className="btn btn-primary btn-sm" onClick={handleAddLevel}>
                      + Level
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={handleAddBreak}>
                      + Break
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">👈</div>
                <h3>Select a structure</h3>
                <p>Choose a blind structure from the left to manage its levels</p>
              </div>
            )}
          </div>
        </div>

        {/* Create/Edit Structure Modal */}
        {showStructureModal && (
          <div className="modal-overlay" onClick={handleCloseStructureModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingStructure ? 'Edit Blind Structure' : 'Create Blind Structure'}</h3>
                <button className="modal-close" onClick={handleCloseStructureModal}>&times;</button>
              </div>

              <form onSubmit={handleStructureSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="name">Structure Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={structureFormData.name}
                      onChange={handleStructureInputChange}
                      placeholder="e.g., Standard Tournament, Fast Structure"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-input"
                      value={structureFormData.description}
                      onChange={handleStructureInputChange}
                      placeholder="Brief description of this blind structure"
                      rows="2"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="blindDuration">Blind Level Duration (minutes) *</label>
                    <input
                      type="number"
                      id="blindDuration"
                      name="blindDuration"
                      className="form-input"
                      value={structureFormData.blindDuration}
                      onChange={handleStructureInputChange}
                      min="1"
                      max="120"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="breakDuration">Break Duration (minutes) *</label>
                    <input
                      type="number"
                      id="breakDuration"
                      name="breakDuration"
                      className="form-input"
                      value={structureFormData.breakDuration}
                      onChange={handleStructureInputChange}
                      min="1"
                      max="60"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="breakFrequency">Break Frequency (levels) *</label>
                    <input
                      type="number"
                      id="breakFrequency"
                      name="breakFrequency"
                      className="form-input"
                      value={structureFormData.breakFrequency}
                      onChange={handleStructureInputChange}
                      min="1"
                      max="20"
                      required
                    />
                    <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
                      Automatic breaks will be inserted every N levels
                    </p>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseStructureModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving
                      ? (editingStructure ? 'Updating...' : 'Creating...')
                      : (editingStructure ? 'Update Structure' : 'Create Structure')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default BlindStructures;
